import * as monaco from "monaco-editor";
import * as React from "react";
import { observer } from "mobx-react-lite";
import { GraphFormStore } from "bundles/BigqueryQueryViewer/store";
import { IDockviewPanelProps } from "dockview";
import { VerticalSplitPane } from "../SplitPane";
import * as styles from "./style.module.css";
import { useEffect, useState } from "react";
import { Editor } from "lib/components/Editor";
import { Button } from "react-bootstrap";
import I18n from "lib/I18n";
import { saveFile } from "lib/components/ExportFile";
import { useSubscriber } from "bundles/BigqueryQueryViewer/lib/pubsub";

export const GraphForm = observer(
  (props: IDockviewPanelProps<{ store: GraphFormStore }>) => {
    const { store } = props.params;
    const subscriber = useSubscriber();

    const [height, setHeight] = useState(props.api.height);
    const [width, setWidth] = useState(props.api.width);
    const [ratio, setRatio] = useState(0.5);
    const [editor, setEditor] = useState<
      monaco.editor.IStandaloneCodeEditor | undefined
    >(undefined);
    const [plotly, setPlotly] = useState<any>(undefined);
    const [plotlyElement, setPlotlyElement] = useState<
      Plotly.PlotlyHTMLElement | undefined
    >(undefined);
    const divRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
      (async () => {
        if (!store.plotlyParams || divRef.current == null) {
          setPlotlyElement(undefined);
          return;
        }
        let Plotly = plotly;
        if (!Plotly) {
          // static importだとviteのbuild時にOOMで死ぬのでdynamic importにしている
          Plotly = await import("plotly.js-dist-min");
          setPlotly(Plotly);
        }
        const plot = await Plotly.newPlot(
          divRef.current,
          store.plotlyParams.data as any,
          store.plotlyParams.layout as any,
          { displaylogo: false, downloadImage: false },
        );
        setPlotlyElement(plot);
      })();
    }, [store.plotlyParams]);

    useEffect(() => {
      if (!plotlyElement || !plotly) {
        return;
      }
      plotly.relayout(plotlyElement, { autosize: true });
    }, [plotlyElement, width, height, ratio]);

    const onEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
      setEditor(editor);
      const model = editor.getModel();
      if (model != null) {
        model.updateOptions({ tabSize: 2 });
      }

      editor.createContextKey("executeCondition", true);
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter,
        onDraw,
        "executeCondition",
      );
      editor.focus();
    };

    useEffect(() => {
      const disposables = [
        props.api.onDidDimensionsChange((e) => {
          setWidth(e.width);
          setHeight(e.height);
        }),
      ];
      return () => {
        for (const disposable of disposables) {
          disposable.dispose();
        }
      };
    }, []);

    useEffect(() => {
      const disposable = subscriber.subscribe(store, (payload) => {
        if (payload == "focus") {
          if (editor) {
            editor.focus();
          }
          props.api.setActive();
        } else {
          if (editor) {
            editor.setSelection(payload);
            editor.revealLine(payload.startLineNumber);
            editor.focus();
          }
          props.api.setActive();
        }
      });
      return () => {
        disposable.dispose();
      };
    }, [editor]);

    useEffect(() => {
      if (store.isRunning) {
        return;
      }
      const dom = document.getElementById(store.domId);
      if (!dom) {
        return;
      }
    }, [store.isRunning]);

    useEffect(() => {
      const dom = document.getElementById(store.domId);
      if (dom) {
        dom.innerHTML = "";
      }
    }, [store.drawId]);

    const onDraw = () => {
      store.run();
    };

    const onDownload = () => {
      if (plotlyElement && plotly) {
        plotly.downloadImage(plotlyElement, {
          format: "png",
          filename: store.queryResult.queryId,
          width: null,
          height: null,
        });
        return;
      }
      const dom = document.getElementById(store.domId);
      if (!dom) {
        return;
      }
      const canvases = dom.getElementsByTagName("canvas");
      if (canvases.length == 0) {
        return;
      }
      canvases[0].toBlob((blob) => {
        if (blob) {
          saveFile(blob, `${store.queryResult.queryId}.png`);
        }
      });
    };

    return (
      <div className={styles.root}>
        <div className={styles.nav}>
          <Button
            variant="primary"
            size="sm"
            className="me-2"
            disabled={store.isRunning}
            onClick={onDraw}
          >
            {I18n.t("bigquery_query_viewer.label.draw")}
          </Button>
          <Button
            variant="success"
            size="sm"
            className="me-2"
            disabled={!store.success}
            onClick={onDownload}
          >
            {I18n.t("bigquery_query_viewer.label.download_image")}
          </Button>
          <span className="text-muted ps-1" style={{ fontSize: "0.8em" }}>
            {store.runningStatus}
          </span>
        </div>
        <VerticalSplitPane
          minRatio={0}
          maxRatio={1}
          ratio={ratio}
          onChangeRatio={(ratio) => setRatio(ratio)}
        >
          <div className={styles.editor}>
            <Editor
              height="100%"
              width={width}
              language="python"
              value={store.script}
              markers={store.markers}
              onChange={(newValue) => (store.script = newValue)}
              onEditorDidMount={onEditorDidMount}
            />
          </div>
          <div id={store.domId} className={styles.graph} ref={divRef} />
        </VerticalSplitPane>
      </div>
    );
  },
);
